import {useCOTracking} from '@aboutyou/tracking';
import {useCustomerBiData} from '@aboutyou/user';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useScayleState} from '../scayle-state/useScayleState';

/**
 * Reports the CO (Datadog) events for users switching from the normal checkout
 * to the Klarna Express Checkout via the "Pay with Klarna" button on the
 * Shipping step. The web component swaps to the Express Review view internally
 * without a URL change, so the only signals we get are the CO
 * `klarna_express` click and `klarna_express_redirect` tracking events
 * (re-dispatched as custom events by mapCheckoutTrackingEvent) and
 * `scayleState.isExpressCheckout` flipping to true once the express review
 * is rendered.
 *
 * ExpressCheckoutEntry fires on the "Pay with Klarna" click event,
 * ExpressCheckoutAuthorization fires on the redirect event, and
 * ExpressCheckoutReviewLoad fires once the scayle state reports the
 * express checkout afterwards. `switched_from_normal_checkout: true`
 * distinguishes this cohort from the regular express checkout entries
 * (basket page / app) in the Datadog funnels.
 *
 * Only mounted for the normal (non transaction-id) checkout web component,
 * as the switch can only happen from there.
 */
export const ReportExpressCheckoutSwitch: React.FC = () => {
  const {track} = useCOTracking();
  const {pc180Score} = useCustomerBiData();
  const isExpressCheckout = useScayleState().data?.isExpressCheckout ?? false;

  const [hasSwitched, setHasSwitched] = useState(false);
  const wasSwitchTrackedRef = useRef(false);
  const wasReviewLoadTrackedRef = useRef(false);

  const handleKlarnaExpressClick = useCallback(() => {
    track({
      type: 'ExpressCheckoutEntry',
      pc180Score,
      expressPaymentKey: 'klarna',
      switched_from_normal_checkout: true,
    });
  }, [pc180Score, track]);

  const handleKlarnaExpressRedirect = useCallback(() => {
    if (wasSwitchTrackedRef.current) {
      return;
    }
    wasSwitchTrackedRef.current = true;

    track({
      type: 'ExpressCheckoutAuthorization',
      pc180Score,
      expressPaymentKey: 'klarna',
      review: true,
      switched_from_normal_checkout: true,
    });

    setHasSwitched(true);
  }, [pc180Score, track]);

  useEffect(() => {
    window.addEventListener(
      KLARNA_EXPRESS_CLICK_EVENT,
      handleKlarnaExpressClick,
    );

    return () => {
      window.removeEventListener(
        KLARNA_EXPRESS_CLICK_EVENT,
        handleKlarnaExpressClick,
      );
    };
  }, [handleKlarnaExpressClick]);

  useEffect(() => {
    window.addEventListener(
      KLARNA_EXPRESS_REDIRECT_EVENT,
      handleKlarnaExpressRedirect,
    );

    return () => {
      window.removeEventListener(
        KLARNA_EXPRESS_REDIRECT_EVENT,
        handleKlarnaExpressRedirect,
      );
    };
  }, [handleKlarnaExpressRedirect]);

  useEffect(() => {
    if (!hasSwitched || !isExpressCheckout) {
      return;
    }
    if (wasReviewLoadTrackedRef.current) {
      return;
    }
    wasReviewLoadTrackedRef.current = true;

    track({
      type: 'ExpressCheckoutReviewLoad',
      pc180Score,
      expressPaymentKey: 'klarna',
      switched_from_normal_checkout: true,
    });
  }, [hasSwitched, isExpressCheckout, pc180Score, track]);

  return null;
};

const KLARNA_EXPRESS_CLICK_EVENT = 'KlarnaExpressClick';

export const dispatchKlarnaExpressClickEvent = () => {
  window.dispatchEvent(new CustomEvent(KLARNA_EXPRESS_CLICK_EVENT));
};

const KLARNA_EXPRESS_REDIRECT_EVENT = 'KlarnaExpressRedirect';

export const dispatchKlarnaExpressRedirectEvent = () => {
  window.dispatchEvent(new CustomEvent(KLARNA_EXPRESS_REDIRECT_EVENT));
};
